import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { 
  auth, 
  db, 
  googleProvider, 
  signInWithPopup, 
  onAuthStateChanged, 
  doc, 
  onSnapshot, 
  setDoc, 
  updateDoc, 
  getDocs,
  getDoc,
  collection,
  query,
  orderBy,
  limit,
  handleFirestoreError, 
  OperationType,
  User
} from '@/firebase';
import { triggerOptimizeEmail } from '@/src/services/EmailService';
import SignUpModal from '@/src/components/SignUpModal';
import SignInModal from '@/src/components/SignInModal';
import LegalBlockingModal from '@/src/components/LegalBlockingModal';
import { 
  fetchTermsOfService, 
  fetchPrivacyPolicy, 
  checkUserAcceptances, 
  initializeLegalDocs 
} from '../services/legalService';
import { getMapsQuota, MapsQuotaStatus } from '../services/apiServices';

interface AuthContextType {
  user: User | null;
  userProfile: any;
  isLoggedIn: boolean;
  isAuthReady: boolean;
  userType: 'individual' | 'company';
  setUserType: (type: 'individual' | 'company') => Promise<void>;
  setUserProfile: (profile: any) => void;
  setIsLoggedIn: (loggedIn: boolean) => void;
  handleLogin: () => Promise<void>;
  handleLogout: () => void;
  isSignUpModalOpen: boolean;
  setIsSignUpModalOpen: (open: boolean) => void;
  isSignInModalOpen: boolean;
  setIsSignInModalOpen: (open: boolean) => void;
  isRecoverKeyModalOpen: boolean;
  setIsRecoverKeyModalOpen: (open: boolean) => void;
  onSignUpPrompt: () => void;
  isPremium: boolean;
  isOptimize: boolean;
  isTeam: boolean;
  isEnterprise: boolean;
  optimizeActive: boolean;
  shouldShowOptimizeNudge: boolean;
  snoozeOptimizeNudge: () => Promise<void>;
  activateOptimize: () => Promise<void>;
  legalAcknowledged: boolean;
  setLegalAcknowledged: (acknowledged: boolean) => void;
  recordLegalAcknowledgment: (deviceFingerprint: string, termsVersion?: string) => Promise<void>;
  reAcceptanceRequired: boolean;
  liveTermsVersion: string;
  livePrivacyVersion: string;
  triggerRecheckAcceptance: () => Promise<void>;
  isDemoGuest: boolean;
  mapsQuota: MapsQuotaStatus | null;
  refreshMapsQuota: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Tier hierarchy: enterprise > team > optimize > premium > free
const TIER_ORDER = ['free', 'premium', 'optimize', 'team', 'enterprise'];
function tierIndex(tier: string): number {
  return TIER_ORDER.indexOf(tier) >= 0 ? TIER_ORDER.indexOf(tier) : 0;
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const isLocalUpdate = useRef(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isRecoverKeyModalOpen, setIsRecoverKeyModalOpen] = useState(false);

  const [userProfile, setUserProfileState] = useState<any>(() => {
    const saved = localStorage.getItem('userProfile');
    return saved ? JSON.parse(saved) : null;
  });

  const [isLoggedIn, setIsLoggedInState] = useState<boolean>(() => {
    return localStorage.getItem('isLoggedIn') === 'true';
  });

  const [isAuthReady, setIsAuthReady] = useState(false);
  const [legalAcknowledged, setLegalAcknowledged] = useState<boolean>(false);
  const [reAcceptanceRequired, setReAcceptanceRequired] = useState(false);
  const [liveTermsVersion, setLiveTermsVersion] = useState('1.0.0');
  const [livePrivacyVersion, setLivePrivacyVersion] = useState('1.0.0');
  const [mapsQuota, setMapsQuota] = useState<MapsQuotaStatus | null>(null);

  const refreshMapsQuota = useCallback(async () => {
    try {
      const quota = await getMapsQuota();
      setMapsQuota(quota);
    } catch (err) {
      console.warn('Failed to load maps quota:', err);
    }
  }, []);

  useEffect(() => {
    refreshMapsQuota();
  }, [user, userProfile?.subscriptionTier, refreshMapsQuota]);

  useEffect(() => {
    initializeLegalDocs();
  }, []);

  const triggerRecheckAcceptance = useCallback(async () => {
    if (!user) {
      setReAcceptanceRequired(false);
      return;
    }

    try {
      const [liveTerms, livePrivacy] = await Promise.all([
        fetchTermsOfService(),
        fetchPrivacyPolicy()
      ]);
      setLiveTermsVersion(liveTerms.version);
      setLivePrivacyVersion(livePrivacy.version);

      const { acceptedTermsVersion, acceptedPrivacyVersion } = await checkUserAcceptances(user.uid);
      if (acceptedTermsVersion !== liveTerms.version || acceptedPrivacyVersion !== livePrivacy.version) {
        setReAcceptanceRequired(true);
      } else {
        setReAcceptanceRequired(false);
      }
    } catch (err) {
      console.warn('Error in compliance check helper:', err);
    }
  }, [user]);

  useEffect(() => {
    triggerRecheckAcceptance();
  }, [user, triggerRecheckAcceptance]);

  useEffect(() => {
    // Clean old demo guest data
    localStorage.removeItem('isDemoGuest');
    localStorage.removeItem('demoGuestLegalAcknowledged');
  }, []);

  useEffect(() => {
    if (!user) {
      setLegalAcknowledged(false);
      return;
    }

    const fetchLegalStatus = async () => {
      try {
        const legalRef = doc(db, 'users', user.uid, 'legal', 'acknowledgment');
        const legalDoc = await getDoc(legalRef);
        if (legalDoc.exists()) {
          const data = legalDoc.data();
          if (data && data.acknowledged === true) {
            setLegalAcknowledged(true);
            return;
          }
        }
        setLegalAcknowledged(false);
      } catch (err) {
        console.warn('Error fetching legal acknowledgment status:', err);
        setLegalAcknowledged(false);
      }
    };
    fetchLegalStatus();
  }, [user]);

  const recordLegalAcknowledgment = async (deviceFingerprint: string, termsVersion: string = '1.0.0') => {
    if (!user) return;

    try {
      const legalRef = doc(db, 'users', user.uid, 'legal', 'acknowledgment');
      await setDoc(legalRef, {
        timestamp: Date.now(),
        termsVersion,
        deviceFingerprint,
        acknowledged: true
      });
      setLegalAcknowledged(true);
    } catch (e) {
      console.error('Error recording legal acknowledgment:', e);
      throw e;
    }
  };

  const [userType, setUserTypeState] = useState<'individual' | 'company'>(() => {
    const saved = localStorage.getItem('userType');
    return (saved === 'individual' || saved === 'company') ? saved : 'individual';
  });

  const setUserType = async (type: 'individual' | 'company') => {
    setUserTypeState(type);
    localStorage.setItem('userType', type);

    if (user) {
      try {
        isLocalUpdate.current = true;
        await updateDoc(doc(db, 'users', user.uid), { userType: type });
        isLocalUpdate.current = false;
      } catch (e) {
        isLocalUpdate.current = false;
        handleFirestoreError(e, OperationType.UPDATE, `users/${user.uid}`);
      }
    }
  };

  const setUserProfile = (profile: any) => {
    setUserProfileState(profile);
    if (profile) {
      localStorage.setItem('userProfile', JSON.stringify(profile));
    } else {
      localStorage.removeItem('userProfile');
    }
  };

  const setIsLoggedIn = (loggedIn: boolean) => {
    setIsLoggedInState(loggedIn);
    localStorage.setItem('isLoggedIn', loggedIn.toString());
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
        setUserProfile(null);
      }
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;

    const userId = user.uid;

    const profileUnsubscribe = onSnapshot(doc(db, 'users', userId), (snapshot) => {
      if (isLocalUpdate.current) return;
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (!data.userType) {
          updateDoc(doc(db, 'users', userId), { userType: 'individual' })
            .catch(e => handleFirestoreError(e, OperationType.UPDATE, `users/${userId}`));
          setUserProfile({ ...data, userType: 'individual' });
        } else {
          setUserProfile(data);
          if (data.userType) {
            setUserType(data.userType as 'individual' | 'company');
          }
        }
      } else {
        const initialProfile = {
          email: user.email,
          name: user.displayName || (userType === 'company' ? 'Enterprise User' : 'Individual User'),
          subscriptionTier: 'free',
          userType: userType,
          updatedAt: new Date().toISOString()
        };
        setDoc(doc(db, 'users', userId), initialProfile)
          .catch(e => handleFirestoreError(e, OperationType.WRITE, `users/${userId}`));
      }
    }, (e) => handleFirestoreError(e, OperationType.GET, `users/${userId}`));

    return () => profileUnsubscribe();
  }, [user, userType]);

  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Login Error:', error);
      throw error;
    }
  };

  const handleLogout = () => {
    auth.signOut().catch(console.error);
    setUser(null);
    setIsLoggedIn(false);
    setUserTypeState('individual');
    setUserProfile(null);
    localStorage.removeItem('isDemoGuest');
    localStorage.removeItem('demoGuestLegalAcknowledged');
    localStorage.removeItem('envesta-tier');
    localStorage.removeItem('user');
    localStorage.removeItem('userProfile');
    localStorage.removeItem('userScans');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userType');
    localStorage.removeItem('landingLoginSource');
  };

  const onSignUpPrompt = () => setIsSignUpModalOpen(true);

  // ─── TIER RESOLUTION ───
  // PRODUCTION: tier comes ONLY from Firestore userProfile.
  const currentTier = (userProfile?.subscriptionTier || 'free').toString().toLowerCase();
  const currentTierIdx = tierIndex(currentTier);

  // Debug logging gated behind DEV only
  if ((import.meta as any).env?.DEV) {
    console.log('[AUTH TIER DEBUG] userProfile?.subscriptionTier:', userProfile?.subscriptionTier);
    console.log('[AUTH TIER DEBUG] resolved currentTier:', currentTier);
    console.log('[AUTH TIER DEBUG] currentTierIdx:', currentTierIdx);
  }

  const isPremium = currentTierIdx >= tierIndex('premium');
  const isOptimize = currentTierIdx >= tierIndex('optimize');
  const isTeam = currentTierIdx >= tierIndex('team');
  const isEnterprise = currentTierIdx >= tierIndex('enterprise');
  const optimizeActive = isOptimize || !!userProfile?.optimizeActive;

  const [shouldShowOptimizeNudge, setShouldShowOptimizeNudge] = useState(false);

  useEffect(() => {
    if (!user || !userProfile || optimizeActive) {
      setShouldShowOptimizeNudge(false);
      return;
    }

    // Check snooze
    if (userProfile.optimizeSnoozedUntil) {
      const snoozeDate = new Date(userProfile.optimizeSnoozedUntil);
      if (snoozeDate > new Date()) {
        setShouldShowOptimizeNudge(false);
        return;
      }
    }

    const evaluateTriggers = async () => {
      try {
        // 1. Check saved ventures count and high scores
        const savedIdeasRef = collection(db, 'users', user.uid, 'savedIdeas');
        const snapshot = await getDocs(savedIdeasRef);
        const ideas = snapshot.docs.map(doc => doc.data());

        const hasThreeVentures = ideas.length >= 3;
        const hasHighScore = ideas.some(idea => (idea.score || 0) > 75);

        // 2. Check first study date
        const scansRef = collection(db, 'users', user.uid, 'scans');
        const scansSnapshot = await getDocs(query(scansRef, orderBy('date', 'asc'), limit(1)));
        let isOldUser = false;
        if (!scansSnapshot.empty) {
          const firstScanDate = new Date(scansSnapshot.docs[0].data().date);
          const sixtyDaysAgo = new Date();
          sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
          isOldUser = firstScanDate < sixtyDaysAgo;
        }

        if (hasThreeVentures || hasHighScore || isOldUser) {
          setShouldShowOptimizeNudge(true);
        }
        // Trigger email sequence
        const triggeredEmails = userProfile?.triggeredEmails || [];
        const triggerEmail = async (condition: string) => {
          if (!triggeredEmails.includes(condition) && user.email) {
            triggerOptimizeEmail(user.email, condition);
            const newTriggered = [...triggeredEmails, condition];
            try {
              await updateDoc(doc(db, 'users', user.uid), { triggeredEmails: newTriggered });
            } catch (e) {
              console.error('Error updating triggered emails:', e);
            }
          }
        };

        if (ideas.length >= 3) triggerEmail('WAR_ROOM_DENSITY');

        let daysSinceFirstScan = 0;
        if (!scansSnapshot.empty) {
          const firstScanDate = new Date(scansSnapshot.docs[0].data().date);
          daysSinceFirstScan = (new Date().getTime() - firstScanDate.getTime()) / (1000 * 3600 * 24);
        }

        if (hasHighScore && daysSinceFirstScan >= 60) triggerEmail('SHELF_LIFE');

      } catch (e) {
        console.error('Error evaluating Optimize triggers:', e);
      }
    };

    evaluateTriggers();
  }, [user, userProfile, optimizeActive]);

  const snoozeOptimizeNudge = async () => {
    if (!user) return;
    const snoozeDate = new Date();
    snoozeDate.setDate(snoozeDate.getDate() + 30);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        optimizeSnoozedUntil: snoozeDate.toISOString()
      });
      setShouldShowOptimizeNudge(false);
    } catch (e) {
      handleFirestoreError(e, OperationType.UPDATE, `users/${user.uid}`);
    }
  };

  const activateOptimize = async () => {
    if (!user) return;
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        optimizeActive: true
      });
      setShouldShowOptimizeNudge(false);
    } catch (e) {
      handleFirestoreError(e, OperationType.UPDATE, `users/${user.uid}`);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      userProfile, 
      isLoggedIn, 
      isAuthReady, 
      userType, 
      setUserType,
      setUserProfile,
      setIsLoggedIn,
      handleLogin, 
      handleLogout,
      isSignUpModalOpen,
      setIsSignUpModalOpen,
      isSignInModalOpen,
      setIsSignInModalOpen,
      isRecoverKeyModalOpen,
      setIsRecoverKeyModalOpen,
      onSignUpPrompt,
      isPremium,
      isOptimize,
      isTeam,
      isEnterprise,
      optimizeActive,
      shouldShowOptimizeNudge,
      snoozeOptimizeNudge,
      activateOptimize,
      legalAcknowledged,
      setLegalAcknowledged,
      recordLegalAcknowledgment,
      reAcceptanceRequired,
      liveTermsVersion,
      livePrivacyVersion,
      triggerRecheckAcceptance,
      isDemoGuest: false,
      mapsQuota,
      refreshMapsQuota
    }}>
      {children}
      {reAcceptanceRequired && user && (
        <LegalBlockingModal
          isOpen={reAcceptanceRequired}
          userId={user.uid}
          requiredTermsVersion={liveTermsVersion}
          requiredPrivacyVersion={livePrivacyVersion}
          onAcceptanceComplete={() => setReAcceptanceRequired(false)}
        />
      )}
      <SignUpModal 
        isOpen={isSignUpModalOpen} 
        onClose={() => setIsSignUpModalOpen(false)} 
        onSwitchToSignIn={() => {
          setIsSignUpModalOpen(false);
          setIsSignInModalOpen(true);
        }}
        onSuccess={(type, userData) => {
          setIsLoggedIn(true);
          setUserType(type);
          setUserProfile(userData);
        }}
        termsVersion={liveTermsVersion}
        privacyVersion={livePrivacyVersion}
      />
      <SignInModal 
        isOpen={isSignInModalOpen} 
        onClose={() => setIsSignInModalOpen(false)}
        onSwitchToSignUp={() => {
          setIsSignInModalOpen(false);
          setIsSignUpModalOpen(true);
        }}
        onRecoverKey={() => {
          setIsSignInModalOpen(false);
          setIsRecoverKeyModalOpen(true);
        }}
        onSuccess={(type, userData) => {
          setIsLoggedIn(true);
          setUserType(type);
          setUserProfile(userData);
        }}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
